import { type ComponentProps, type ReactNode } from 'react';

import { type PhoebeAgentRunPendingEventRead } from '@apps/web/components/phoebe_chat/streaming_types';
import {
    BackgroundWorkTray,
    type RunWatch,
    selectBackgroundWork,
    type V3ChatPerformanceAttempt,
    V3ChatSurface,
} from '@apps/web/features/phoebe_chat_v3';

const STARTER_PROMPTS: ReadonlyArray<string> = [
    'Catch me up',
    'Do I have any open shifts this week?',
    'Summarize recent callouts',
];

export type ChatRenderer = 'legacy' | 'loading' | 'native';

export type ChatPerformanceAttempt = V3ChatPerformanceAttempt;

export function selectChatRenderer({
    agentV3Enabled,
    isRunNativeProductChat,
    savedRunIsAgentV3,
}: {
    readonly agentV3Enabled: boolean;
    readonly isRunNativeProductChat: boolean;
    readonly savedRunIsAgentV3: boolean | null | undefined;
}): ChatRenderer {
    if (!isRunNativeProductChat) {
        return 'legacy';
    }
    if (savedRunIsAgentV3 === undefined) {
        return 'loading';
    }
    return (savedRunIsAgentV3 ?? agentV3Enabled) ? 'native' : 'legacy';
}

export function ChatComposerStatus({
    renderer,
    pendingEvents,
    watches,
    runStatus,
    nowMs,
    timezone,
}: {
    readonly renderer: ChatRenderer;
    readonly pendingEvents: ReadonlyArray<PhoebeAgentRunPendingEventRead>;
    readonly watches: ReadonlyArray<RunWatch>;
    readonly runStatus: string | null;
    readonly nowMs: number;
    readonly timezone: string;
}) {
    if (renderer !== 'native') {
        return null;
    }
    const rows = selectBackgroundWork({
        pendingEvents,
        watches,
        runStatus,
        nowMs,
    });
    if (rows.length === 0) {
        return null;
    }

    return <BackgroundWorkTray rows={rows} timezone={timezone} />;
}

type NativeSurfaceProps = Omit<
    ComponentProps<typeof V3ChatSurface>,
    'starterPrompts'
>;

/**
 * The only shared component allowed to import native v3. The caller supplies
 * its legacy React tree; this host mounts either that tree or the native v3
 * surface so renderer imports cannot spread through shared code.
 */
export function ChatSurfaceHost({
    renderer,
    legacySurface,
    unresolvedSurface,
    nativeAlternateSurface,
    nativeSurfaceKey,
    nativeSurfaceProps,
}: {
    readonly renderer: ChatRenderer;
    readonly legacySurface: ReactNode;
    readonly unresolvedSurface: ReactNode;
    readonly nativeAlternateSurface: ReactNode;
    readonly nativeSurfaceKey: string;
    readonly nativeSurfaceProps: NativeSurfaceProps;
}) {
    if (renderer === 'legacy') {
        return legacySurface;
    }
    if (renderer === 'loading') {
        return unresolvedSurface;
    }
    if (nativeAlternateSurface !== null) {
        return nativeAlternateSurface;
    }
    return (
        <V3ChatSurface
            key={nativeSurfaceKey}
            {...nativeSurfaceProps}
            starterPrompts={STARTER_PROMPTS}
        />
    );
}
